/**
 * Scoring constants (spec section 2).
 *
 * Rung 1 pays `BASE_POINTS` plus a speed bonus that decays linearly from
 * `SPEED_BONUS_MAX` to 0 across `SPEED_BONUS_WINDOW_MS`, the whole award then
 * scaled by the PINPOINT ATTEMPT multiplier. Every subsequent rung multiplies
 * the whole pot, so the pot is cumulative.
 *
 * ===========================================================================
 * THE SPEED-BONUS SWING TABLE — the one number the owner will retune by feel.
 * ===========================================================================
 *
 * `SPEED_BONUS_MAX` sits INSIDE the pot, so it compounds through every rung
 * above it. That makes it the single most leveraged constant in the game, and
 * it was mis-set: at 100 an instant pinpoint literally DOUBLED a full run, so
 * the pinpoint race, not the knowledge ladder, was the dominant scoring lever
 * — backwards for a game whose thesis is the betting tension.
 *
 *   rung-1 award = round((BASE_POINTS + speedBonus(latency)) x attemptMult)
 *   cumulative multiplier = prod(RUNG_MULTIPLIERS[2..rung])
 *
 *                        rung-1     Explorer      Navigator /
 *   pinpoint             award      full x5     Cartographer full x15
 *   ------------------------------------------------------------------
 *   instant  (0 ms)        150         750               2,250
 *   fast     (2.5 s)       138         690               2,070
 *   even     (5 s)         125         625               1,875
 *   slow     (>=10 s)      100         500               1,500
 *
 *   SWING, slow -> instant:  x1.50 at every ladder depth.
 *
 * Under the old SPEED_BONUS_MAX = 100 the same table read 1,500 vs 3,000 — a
 * x2.00 swing. 50 keeps speed clearly worth chasing while leaving knowledge
 * the bigger lever: one extra rung cleared is worth x2 to x3, which now
 * dominates the x1.5 that perfect reflexes buy.
 *
 * To retune: change SPEED_BONUS_MAX and nothing else. The swing is exactly
 * `(BASE_POINTS + SPEED_BONUS_MAX) / BASE_POINTS`, independent of tier and of
 * ladder depth, because the bonus is inside the base the multipliers compound.
 *
 * `packages/shared/src/scoring.test.ts` asserts this table, so an edit that
 * changes the swing fails the gate rather than drifting quietly.
 */

export const SCORING = {
  /** Flat points for a correct rung-1 pinpoint. */
  BASE_POINTS: 100,
  /**
   * Maximum speed bonus, awarded at latency 0.
   *
   * 100 -> 50 (playtest change 1). See the swing table above: this halves the
   * reflex lever without removing it. Full-ladder swing 1,500 -> 2,250, where
   * it used to be 1,500 -> 3,000.
   */
  SPEED_BONUS_MAX: 50,
  /** Speed bonus reaches 0 at this latency. Linear decay in between. */
  SPEED_BONUS_WINDOW_MS: 10_000,
  /**
   * Pot multiplier indexed BY RUNG. Index 0 is padding and never read.
   * Index 1 is the pinpoint rung (no multiplier applied).
   *
   * UNTOUCHED by the playtest changes, deliberately. The golden round does not
   * edit this table — it scales the TERMINAL rung's entry by
   * `GOLDEN_MULTIPLIER_FACTOR`; see `rungMultiplier`.
   */
  RUNG_MULTIPLIERS: [1, 1, 2, 2.5, 3] as const,
  /** Maximum ladder depth across all tiers; bounds RUNG_MULTIPLIERS. */
  MAX_RUNG: 4,
  /**
   * A pinpoint faster than this on any entity is statistically impossible and
   * is auto-flagged for review (spec 8.7).
   */
  IMPOSSIBLE_PINPOINT_MS: 250,

  // -------------------------------------------------------------------------
  // Change 4 — pinpoint retries, and what a miss costs
  // -------------------------------------------------------------------------

  /**
   * Award multiplier by PINPOINT ATTEMPT. Index 0 is attempt 1.
   *
   * WHY A POINT PENALTY AND NOT A TIME-ONLY ONE. Time-only was rejected by the
   * owner with a sound argument: "if I know the country is really close to an
   * area I can just rapid fire guess the three countries right next to each
   * other with little penalty if it were time only." Adjacent-country spamming
   * has to be expensive, and the speed-bonus decay alone does not make it so —
   * three taps take about a second and cost about 5 points of bonus.
   *
   * THE MULTIPLIER SCALES THE WHOLE RUNG-1 AWARD, base and speed bonus alike,
   * not just the base. Two reasons. It is the version a player can hold in
   * their head — "attempt 3 pays 15% of what attempt 1 would have paid at this
   * instant" — which matters because change 4 requires the price be VISIBLE
   * before they commit. And because the rung-1 award compounds through every
   * rung above it, the penalty compounds too, which is what makes spamming
   * genuinely bad rather than merely suboptimal.
   *
   * MEASURED (Explorer, ladder depth 3, cumulative x5):
   *
   *   play                                  rung-1 award    banked full ladder
   *   -----------------------------------------------------------------------
   *   one considered guess at 5.0 s              125               625
   *   one considered guess at 8.0 s              110               550
   *   hit on attempt 2 at 3.0 s                   54               270
   *   RAPID-FIRE, hit on attempt 3 at 1.5 s       22               110
   *   RAPID-FIRE, hit on attempt 3 at 0.8 s       22               110
   *   miss all three                               0                 0
   *
   * ===========================================================================
   * WHY 0.6/0.3 BECAME 0.4/0.15, AND THE THING NO LADDER CAN FIX
   * ===========================================================================
   *
   * The brief asked for a ladder tuned so that "three rapid adjacent guesses is
   * clearly worse than one considered guess". The published table used to be
   * defended by comparing PAYOFF GIVEN A HIT — 215 against 625 — and that is
   * the wrong quantity. The exploit is a strategy chosen BEFORE you know
   * whether you will land it, so the comparison has to be EV against EV, and
   * on that comparison the shipped ladder lost:
   *
   *   ladder            3-way spam EV   considered(p=.5, 5s)   spam premium
   *   -------------------------------------------------------------------
   *   [1, 0.6, 0.3]          465                 313              1.49x
   *   [1, 0.5, 0.25]         428                 313              1.37x
   *   [1, 0.4, 0.15]         380                 313              1.22x   <--
   *   [1, 0.3, 0.10]         343                 313              1.10x
   *   [1, 0.25, 0.05]        320                 313              1.02x
   *
   * AND IT CANNOT BE DRIVEN BELOW 1.00x BY ANY LADDER, which is worth stating
   * plainly so nobody spends another pass trying. Take the honest hard case: a
   * player who has narrowed the target to TWO adjacent countries and cannot
   * separate them. Tapping both is the same first tap as tapping one, plus a
   * second tap that pays a non-negative amount. Strictly better, at every
   * multiplier, by construction:
   *
   *   ladder            tap both   tap once   ratio
   *   ------------------------------------------------
   *   [1, 0.6, 0.3]        590        313      1.89x
   *   [1, 0.4, 0.15]       515        313      1.65x
   *   [1, 0.25, 0.05]      463        313      1.48x
   *   [1, 0.01, 0.01]      373        313      1.19x
   *
   * A retry allowance converts BUST RISK into POINTS LOSS, and bust risk is
   * the expensive half. Only a NEGATIVE multiplier would reverse that, and a
   * miss that takes points off a pot the player has not built yet is not a
   * thing this scoring model can express. THE OWNER SHOULD KNOW THIS IS A
   * PROPERTY OF CHANGE 4 ITSELF: three attempts means a player who can narrow
   * to three candidates is never punished for not knowing which. If that is
   * not acceptable, the knob is `maxAttempts` in `TIER_CONFIG`, not this table.
   *
   * WHAT THE RETUNE DID BUY, and it is the more important number: rung-1 SKILL
   * EXPRESSION at Explorer. Deliberating does not consume an attempt, so under
   * the old ladder its only value was reordering your candidates, and perfect
   * pinpoint knowledge was worth almost nothing:
   *
   *   ladder            no knowledge   certain @ 11s   what knowing is worth
   *   ---------------------------------------------------------------------
   *   [1, 0.6, 0.3]         465             500                 +8%
   *   [1, 0.4, 0.15]        380             500                +32%
   *
   * A fourfold increase in what learning the globe pays, at the tier that most
   * needs to learn it. That is the trade this constant is making: the spam
   * premium falls by half and can go no lower, while the reward for actually
   * knowing where the country is quadruples.
   *
   * `packages/shared/src/scoring.test.ts` asserts this ladder, that it is
   * strictly decreasing, and both measured bounds above.
   */
  PINPOINT_ATTEMPT_MULTIPLIERS: [1, 0.4, 0.15] as const,

  // -------------------------------------------------------------------------
  // Change 6 — the golden round
  // -------------------------------------------------------------------------

  /**
   * What the golden round multiplies its TERMINAL rung's multiplier by.
   *
   * 3, giving x9 on a rung-4 ladder (Navigator, Cartographer) and x7.5 on
   * Explorer's rung-3 ladder. "Triple what a normal rung 4 pays" was the
   * owner's instruction; expressing it as a FACTOR rather than a hardcoded 9
   * is what makes it mean the same thing on a three-rung ladder.
   */
  GOLDEN_MULTIPLIER_FACTOR: 3,

  /**
   * The rung-4 case of the above, spelled out because it is the number the
   * design conversation used. Equals RUNG_MULTIPLIERS[4] x
   * GOLDEN_MULTIPLIER_FACTOR = 3 x 3 = 9. Asserted in the tests.
   */
  GOLDEN_RUNG_MULTIPLIER: 9,

  /** Exactly one round per match is golden (spec section 2, change 6). */
  GOLDEN_ROUNDS_PER_MATCH: 1,
} as const;

/**
 * ===========================================================================
 * EV CONSEQUENCES OF EVERY CONSTANT ABOVE. Read this before "fixing" anything.
 * ===========================================================================
 *
 * Let B be the rung-1 award. The pot after rung r is B x prod(mult[2..r]):
 *
 *   rung        cumulative      pot @ B=100      pot @ B=150
 *   ---------------------------------------------------------
 *   1                x1              100              150
 *   2 (x2)           x2              200              300
 *   3 (x2.5)         x5              500              750
 *   4 (x3)          x15            1,500            2,250
 *   4 GOLDEN (x9)   x45            4,500            6,750
 *   3 GOLDEN (x7.5) x15            1,500            2,250   (Explorer)
 *
 * A push from rung r-1 to rung r is break-even at
 * P = pot(r-1) / pot(r) = 1 / mult[r]:
 *
 *   push to rung 2            break-even P = 50.0%
 *   push to rung 3            break-even P = 40.0%
 *   push to rung 4            break-even P = 33.3%
 *   push to GOLDEN rung 4     break-even P = 11.1%
 *   push to GOLDEN rung 3     break-even P = 13.3%   (Explorer)
 *
 * RUNG 2 IS NOW DELIBERATELY EV-NEUTRAL FOR A PURE GUESSER. DO NOT "FIX" IT.
 * ---------------------------------------------------------------------------
 * Change 5 makes rung 2 a TRUE/FALSE statement. A blind guess succeeds 50% of
 * the time against a 50.0% break-even, so a coin-flipper's EV on the rung-2
 * push is exactly zero — neither punished nor rewarded.
 *
 * This was raised with the owner as a balance concern and he CHOSE it, with
 * reasoning that stands and that this comment exists to preserve:
 *
 *   1. Knowledge still wins more often than guessing. A player who knows the
 *      statement is right ~85% of the time earns 0.85 x 2 = 1.70x on the push,
 *      against the guesser's 1.00x. The gap between knowing and guessing is
 *      what the rung pays for, and it is intact.
 *   2. It is only rung 2 — the CHEAP rung. The pot at stake is 2B, the
 *      smallest bet in the game.
 *   3. It buys accessibility exactly where the game needs it: a less
 *      knowledgeable player now has a reason to climb into rungs 3 and 4,
 *      which is where knowledge is supposed to separate players and where the
 *      break-evens (40%, 33.3%) sit well above any blind floor. Busting out at
 *      rung 2 every match is how a new player stops playing.
 *
 * The rung-2 T/F generator's own guard against this becoming a FREE win rather
 * than a neutral one is the true/false BALANCE requirement: if materially more
 * than half of generated statements were true, "always answer true" would beat
 * the coin flip and the rung would become +EV for someone who noticed. The
 * balance is measured over >=10,000 generated statements and asserted in
 * `@geogame/rules`.
 *
 * WHY GOLDEN'S 11.1% BREAK-EVEN IS NOT A BUG EITHER.
 * ---------------------------------------------------------------------------
 * x9 was the owner's chosen payout. It does mean the golden push is correct
 * for any player above a one-in-nine shot, so the golden round is closer to an
 * EVENT than to a dilemma — and that is what "fire animation, it should feel
 * like an event" asks for. The tension it removes from one round in seven is
 * paid for by the difficulty of the rung itself: an OPEN NUMERIC answer,
 * against rung 4's measured 0.66-0.72 P(success) as a four-option multiple
 * choice.
 *
 * "0% BLIND-GUESS FLOOR" IS THE INTENT AND NOT THE MEASUREMENT, and the
 * difference matters enough to state here as well as in the generator.
 * Removing the options removes the 25% that came from having four of them; it
 * does not make the answer distribution uniform. Measured EV of a BLIND push
 * into the golden terminal, against simply banking:
 *
 *   Explorer 0.75x -EV   Navigator 1.05x +EV   Cartographer 0.94x -EV
 *   (a normal four-option terminal is 0.75x -EV, for comparison)
 *
 * That is after closing the two generator faults that put it at 1.18x / 1.24x
 * / 1.20x — the 1955 UN package deal and the low-entropy `land-borders`
 * quantity, both documented in `@geogame/rules`. Navigator's residual follows
 * from this constant meeting a distribution whose best constants sit near one
 * in six, and it is the owner's to decide about: GOLDEN_MULTIPLIER_FACTOR is
 * the single knob, and 2 would put the break-even at 16.7% and the blind push
 * clearly -EV everywhere.
 */

export type Decision = 'push' | 'bank' | 'timeout' | 'bust';

export const ROUND_OUTCOMES = ['banked', 'busted', 'timeout', 'missed'] as const;
export type RoundOutcome = (typeof ROUND_OUTCOMES)[number];

/**
 * Where a seat is in the ladder. Lives here rather than in `@geogame/rules`
 * because the wire needs it too: `match.sync` (spec 8.6) replays a seat's
 * authoritative phase onto a reconnecting client, and `@geogame/shared` is the
 * package both sides of that already depend on. `@geogame/rules` re-exports it
 * so the reducer's public type surface is unchanged.
 */
export const MATCH_PHASES = [
  'idle',
  'armed',
  'pinpoint',
  'decision',
  'probe',
  'round-end',
  'match-end',
] as const;
export type MatchPhase = (typeof MATCH_PHASES)[number];

export function isMatchPhase(v: unknown): v is MatchPhase {
  return typeof v === 'string' && (MATCH_PHASES as readonly string[]).includes(v);
}

/**
 * Linear-decay speed bonus for a rung-1 pinpoint. Clamped to
 * [0, SPEED_BONUS_MAX]; negative latencies (clock skew) award the maximum.
 */
export function speedBonus(latencyMs: number): number {
  if (!Number.isFinite(latencyMs) || latencyMs <= 0) return SCORING.SPEED_BONUS_MAX;
  if (latencyMs >= SCORING.SPEED_BONUS_WINDOW_MS) return 0;
  const remaining = 1 - latencyMs / SCORING.SPEED_BONUS_WINDOW_MS;
  return Math.round(SCORING.SPEED_BONUS_MAX * remaining);
}

/**
 * Multiplier applied when a player successfully clears `rung`.
 *
 * `goldenTerminal` is true only when this rung is the TERMINAL rung of a
 * GOLDEN round; the caller knows the ladder depth and this function does not,
 * so the decision is made there and asserted here by the tests.
 */
export function rungMultiplier(rung: number, goldenTerminal = false): number {
  const m = SCORING.RUNG_MULTIPLIERS[rung];
  const base = typeof m === 'number' ? m : 1;
  return goldenTerminal ? base * SCORING.GOLDEN_MULTIPLIER_FACTOR : base;
}

// ---------------------------------------------------------------------------
// Pinpoint attempts (change 4)
// ---------------------------------------------------------------------------

/**
 * How many pinpoint attempts a seat gets and what each one pays. A property of
 * the TIER (`TIER_CONFIG`), never of the player and never of a "mode": the
 * tier axis already exists, and a mode axis would multiply queues and
 * leaderboards for no gain. Both seats in a match always get the same policy,
 * because tier is a property of the match.
 */
export interface PinpointAttemptPolicy {
  /** Always equal to `multipliers.length`. */
  readonly maxAttempts: number;
  /** Award multiplier by attempt. `multipliers[0]` is attempt 1. */
  readonly multipliers: readonly number[];
}

/**
 * The attempt picture as the PLAYER sees it, before they commit to the next
 * tap. Change 4 requires the remaining attempts and their value be visible so
 * the retry is an informed choice rather than a free reroll.
 */
export interface PinpointAttemptState {
  /** 1-based index of the attempt this state describes the outcome of. */
  readonly attempt: number;
  readonly maxAttempts: number;
  /** Attempts left AFTER `attempt`. Zero means the rung is resolved. */
  readonly attemptsRemaining: number;
  /** Award multiplier the NEXT attempt would be scaled by. 0 when none left. */
  readonly nextMultiplier: number;
  /**
   * What the next attempt would pay IF IT LANDED AT THE LATENCY THIS STATE WAS
   * COMPUTED AT — i.e. right now. It keeps falling as the speed bonus decays,
   * so the client should treat it as a live figure, not a promise. 0 when no
   * attempt remains.
   */
  readonly nextMaxPoints: number;
}

/** Award multiplier for a 1-based attempt number. 0 past the allowance. */
export function pinpointAttemptMultiplier(policy: PinpointAttemptPolicy, attempt: number): number {
  if (!Number.isInteger(attempt) || attempt < 1) return 0;
  return policy.multipliers[attempt - 1] ?? 0;
}

/**
 * The full rung-1 award: base plus speed bonus, scaled by the attempt
 * multiplier, rounded ONCE at the end so a x0.6 cannot leak fractional points
 * into the scoreboard.
 */
export function pinpointAward(latencyMs: number, attemptMultiplier: number): number {
  if (!Number.isFinite(attemptMultiplier) || attemptMultiplier <= 0) return 0;
  return Math.round((SCORING.BASE_POINTS + speedBonus(latencyMs)) * attemptMultiplier);
}

/** Build the player-visible attempt state after `attempt` has been graded. */
export function pinpointAttemptState(
  policy: PinpointAttemptPolicy,
  attempt: number,
  latencyMs: number,
): PinpointAttemptState {
  const remaining = Math.max(0, policy.maxAttempts - attempt);
  const nextMultiplier = remaining > 0 ? pinpointAttemptMultiplier(policy, attempt + 1) : 0;
  return {
    attempt,
    maxAttempts: policy.maxAttempts,
    attemptsRemaining: remaining,
    nextMultiplier,
    nextMaxPoints: remaining > 0 ? pinpointAward(latencyMs, nextMultiplier) : 0,
  };
}
